import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { FaUserCircle, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import './article_view.css';

function ArticleView() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuthStore();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  // # Add edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedArticle, setEditedArticle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // # Add delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // # Add iframe URL state to handle theme parameters
  const [iframeUrl, setIframeUrl] = useState('');
  // # Create ref for iframe
  const iframeRef = useRef(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // # Load theme preference
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }

        // # Load article data
        const technicalsDoc = await getDoc(doc(db, 'technicals', 'articles'));
        if (technicalsDoc.exists()) {
          const articles = technicalsDoc.data();
          const articleData = articles[articleId];
          
          if (articleData) {
            setArticle(articleData);
            // # Initialize edited article data
            setEditedArticle({
              title: articleData.title || '',
              description: articleData.description || '',
              url: articleData.url || '',
            });
          } else {
            setError('Article not found');
          }
        } else {
          setError('Article not found');
        }
      } catch (error) {
        console.error('Error loading article:', error);
        setError('Failed to load article');
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [articleId, user]);

  // # Update iframe URL when article or theme changes
  useEffect(() => {
    if (article?.url) {
      // # Check if URL already has parameters
      const baseUrl = article.url;
      const urlObj = new URL(baseUrl);
      
      // # Set theme parameter based on current theme
      urlObj.searchParams.set('theme', isDarkMode ? 'dark' : 'light');
      
      // # Force dark/light mode in notion
      if (baseUrl.includes('notion.so') || baseUrl.includes('notion.site')) {
        if (isDarkMode) {
          urlObj.searchParams.set('mode', 'dark');
        } else {
          urlObj.searchParams.set('mode', 'light');
        }
      }
      
      // # Add parameters to disable features that might cause errors - copied from chapter_iframe.js
      urlObj.searchParams.set('disableDatabase', 'true');
      urlObj.searchParams.set('disableSqlite', 'true');
      urlObj.searchParams.set('disableStorage', 'true');
      urlObj.searchParams.set('disableCookies', 'true');
      
      setIframeUrl(urlObj.toString());
    }
  }, [article, isDarkMode]);

  // # Function to handle iframe load events
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Apply theme directly to iframe
        applyThemeToIframe(isDarkMode);
        
        // # Try to access iframe content to inject error suppression and styling
        const style = document.createElement('style');
        style.textContent = `
          /* Hide error messages and UI elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-link-token,
          .notion-collection-card__content,
          .notion-header__icon-wrapper {
            display: none !important;
          }
          
          /* Force theme colors */
          body, html {
            background-color: ${isDarkMode ? '#121212' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
          }
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);
              
              // # Also try to intercept console methods in the iframe
              const script = document.createElement('script');
              script.textContent = `
                // Block network requests that might leak URLs
                const originalFetch = window.fetch;
                window.fetch = function(url, options) {
                  // Allow same origin requests
                  return originalFetch.apply(this, arguments);
                };
              `;
              iframeRef.current.contentDocument.head.appendChild(script);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };
  
  // # Function to apply theme directly to iframe
  const applyThemeToIframe = (darkMode) => {
    if (iframeRef.current) {
      try {
        // # Update iframe data-theme attribute
        iframeRef.current.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        
        // # Update iframe style - remove filters, use proper CSS variables
        iframeRef.current.style.colorScheme = darkMode ? 'dark' : 'light';
        iframeRef.current.style.backgroundColor = darkMode ? '#121212' : 'white';
        
        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              const style = document.createElement('style');
              if (darkMode) {
                // # Dark mode styles without filters
                style.textContent = `
                  /* # Dark mode base styles */
                  body, html {
                    background-color: #121212 !important;
                    color-scheme: dark !important;
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Dark mode text colors */
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Dark mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: #121212 !important;
                  }
                  
                  /* # Dark mode for block elements */
                  .notion-callout-block {
                    background-color: #1a1a1a !important;
                    border-color: rgba(255, 255, 255, 0.05) !important;
                  }
                  
                  /* # Tables in dark mode */
                  .notion-collection-table {
                    background-color: #121212 !important;
                    border-color: rgba(255, 255, 255, 0.05) !important;
                  }
                  
                  .notion-collection-table th,
                  .notion-collection-table td {
                    border-color: rgba(255, 255, 255, 0.05) !important;
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Links in dark mode */
                  a, a:visited {
                    color: #64B5F6 !important;
                  }
                `;
              } else {
                // # Light mode styles
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    color: #2c3e50 !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #2c3e50 !important;
                  }
                  
                  /* # Light mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: white !important;
                  }
                  
                  /* # Light mode for block elements */
                  .notion-callout-block {
                    background-color: #f8f9fa !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  /* # Tables in light mode */
                  .notion-collection-table {
                    background-color: white !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  a, a:visited {
                    color: #0b6bcb !important;
                  }
                `;
              }
              iframeRef.current.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Apply theme directly to iframe
      applyThemeToIframe(newTheme);
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Handle edit button click
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // # Handle delete button click
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // # Handle modal close
  const handleCloseModal = () => {
    setShowEditModal(false);
    // # Reset form with original data
    if (article) {
      setEditedArticle({
        title: article.title || '',
        description: article.description || '',
        url: article.url || '',
      });
    }
  };

  // # Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedArticle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Handle article update
  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      setIsSubmitting(true);

      // # Validate inputs
      if (!editedArticle.title.trim() || !editedArticle.description.trim() || !editedArticle.url.trim()) {
        throw new Error('Please fill in all fields');
      }

      // # Create updated article data
      const updatedArticleData = {
        ...article,
        ...editedArticle,
        updatedAt: Date.now(),
      };

      // # Update Firestore
      const technicalsRef = doc(db, 'technicals', 'articles');
      await updateDoc(technicalsRef, {
        [articleId]: updatedArticleData
      });

      // # Update local state
      setArticle(updatedArticleData);

      // # Close modal
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating article:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // # Handle article deletion
  const handleDeleteArticle = async () => {
    if (!isAdmin) return;

    try {
      setIsDeleting(true);
      
      // # Get current articles
      const technicalsRef = doc(db, 'technicals', 'articles');
      const technicalsDoc = await getDoc(technicalsRef);
      
      if (technicalsDoc.exists()) {
        const articlesData = technicalsDoc.data();
        
        // # Create a copy and remove the article
        const updatedArticles = { ...articlesData };
        delete updatedArticles[articleId];
        
        // # Update Firestore without the deleted article
        await setDoc(technicalsRef, updatedArticles);
        
        // # Navigate back to articles list
        navigate('/articles');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      alert(`Error: ${error.message}`);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`artcl_view_loading ${isDarkMode ? 'artcl_view_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="artcl_view_loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`artcl_view_error ${isDarkMode ? 'artcl_view_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="artcl_view_error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.history.back()}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={`artcl_view_not-found ${isDarkMode ? 'artcl_view_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="artcl_view_not-found-content">
          <h2>Article Not Found</h2>
          <p>The article you're looking for doesn't exist.</p>
          <button onClick={() => window.history.back()}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`artcl_view_container ${isDarkMode ? 'artcl_view_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      {/* # Author pill with admin controls */}
      <div className="artcl_view_author-pill">
        {article.authorPhotoURL ? (
          <img 
            src={article.authorPhotoURL} 
            alt={article.authorName} 
            className="artcl_view_author-avatar"
          />
        ) : (
          <FaUserCircle className="artcl_view_author-avatar-fallback" />
        )}
        <span className="artcl_view_author-name">{article.authorName}</span>
        
        {/* # Admin controls */}
        {isAdmin && (
          <div className="artcl_view_admin-controls">
            <button 
              className="artcl_view_edit-button" 
              onClick={handleEditClick}
              aria-label="Edit article"
            >
              <FaEdit />
            </button>
            <button 
              className="artcl_view_delete-button" 
              onClick={handleDeleteClick}
              aria-label="Delete article"
            >
              <FaTrash />
            </button>
          </div>
        )}
      </div>
      
      {/* # Iframe container - adjusted to overlap with header */}
      <div className={`artcl_view_iframe-container ${isDarkMode ? 'artcl_view_dark-mode-container' : ''}`}>
        {/* # Error suppressor element */}
        <div className="artcl_view_error-suppressor" aria-hidden="true"></div>
        
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title={article.title}
          className={isDarkMode ? 'artcl_view_dark-mode-iframe' : 'artcl_view_light-mode-iframe'}
          allowFullScreen
          referrerPolicy="no-referrer"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          data-theme={isDarkMode ? 'dark' : 'light'}
          style={{
            colorScheme: isDarkMode ? 'dark' : 'light',
            backgroundColor: isDarkMode ? '#121212' : 'white'
          }}
          loading="lazy"
          importance="high"
          onLoad={handleIframeLoad}
        />
      </div>

      {/* # Edit Modal */}
      {showEditModal && (
        <div className="artcl_view_modal-overlay" onClick={handleCloseModal}>
          <div className="artcl_view_modal" onClick={e => e.stopPropagation()}>
            <button className="artcl_view_modal-close" onClick={handleCloseModal}>
              <FaTimes />
            </button>
            <h2>Edit Article</h2>
            <form onSubmit={handleUpdateArticle}>
              <div className="artcl_view_form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={editedArticle.title}
                  onChange={handleInputChange}
                  placeholder="Enter article title"
                  required
                />
              </div>
              <div className="artcl_view_form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={editedArticle.description}
                  onChange={handleInputChange}
                  placeholder="Enter article description"
                  required
                />
              </div>
              <div className="artcl_view_form-group">
                <label htmlFor="url">Notion Embed URL</label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={editedArticle.url}
                  onChange={handleInputChange}
                  placeholder="Enter Notion embed URL"
                  required
                />
              </div>
              <button 
                type="submit" 
                className="artcl_view_submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Article'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* # Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="artcl_view_modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="artcl_view_modal artcl_view_delete-modal" onClick={e => e.stopPropagation()}>
            <h2>Delete Article?</h2>
            <p>Are you sure you want to delete this article? This action cannot be undone.</p>
            <div className="artcl_view_delete-actions">
              <button 
                className="artcl_view_cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="artcl_view_delete-confirm-btn"
                onClick={handleDeleteArticle}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleView; 